/**
 * ColumnMenu — admin tools on a board column: rename, colour, move, kind, delete.
 * APG menu semantics: plain `menuitem`s, `separator`s between sections, and
 * the colour / kind pickers as `group`s of `menuitemradio`s. Left/Right in
 * the kind group change the selection locally and PATCH once typing stops.
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Menu, MenuItem } from '@/components/Menu';
import { COALESCE_MS, ColorSwatches } from '@/features/stages/ColorSwatches';
import { STAGE_KINDS } from '@/features/stages/stage-colors';
import type { StageAdmin } from '@/features/stages/use-stage-admin';
import type { Stage, StageKind } from './types';

function Separator() {
  return <div role="separator" className="my-1 border-t border-gray-100" />;
}

export function ColumnMenu({
  stage,
  index,
  total,
  count,
  admin,
  onRename,
}: {
  stage: Stage;
  index: number;
  total: number;
  count: number;
  admin: StageAdmin;
  onRename: () => void;
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const [pendingKind, setPendingKind] = useState<StageKind | null>(null);
  const kindTimer = useRef<number | null>(null);
  const kind = pendingKind ?? stage.kind;
  useEffect(() => () => { if (kindTimer.current) window.clearTimeout(kindTimer.current); }, []);
  useEffect(() => { if (pendingKind && pendingKind === stage.kind) setPendingKind(null); }, [stage.kind, pendingKind]);

  const pickKind = (next: StageKind, immediate: boolean) => {
    if (kindTimer.current) window.clearTimeout(kindTimer.current);
    kindTimer.current = null;
    setPendingKind(next);
    if (immediate) admin.setKind(stage, next);
    else kindTimer.current = window.setTimeout(() => { kindTimer.current = null; admin.setKind(stage, next); }, COALESCE_MS);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Options for ${stage.name}`}
        className="-mb-3 -mt-2 flex h-10 w-10 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
      >
        <MoreHorizontal size={16} strokeWidth={2} aria-hidden />
      </button>
      <Menu open={open} onClose={close} label={`${stage.name} options`} className="w-[260px]">
        <MenuItem onSelect={() => { close(); onRename(); }}>
          <Pencil size={14} strokeWidth={2} aria-hidden /> Rename
        </MenuItem>
        <MenuItem disabled={index === 0} onSelect={() => { close(); admin.move(stage, -1); }}>
          <ArrowLeft size={14} strokeWidth={2} aria-hidden /> Move left
        </MenuItem>
        <MenuItem disabled={index === total - 1} onSelect={() => { close(); admin.move(stage, 1); }}>
          <ArrowRight size={14} strokeWidth={2} aria-hidden /> Move right
        </MenuItem>

        <Separator />
        <div className="px-2.5 pb-0.5 pt-1.5 text-[12px] font-medium text-muted" aria-hidden>
          Colour
        </div>
        <div className="px-2.5 pb-1">
          <ColorSwatches inMenu value={stage.color} onChange={(hex) => admin.recolor(stage, hex)} />
        </div>

        <Separator />
        <div className="px-2.5 pb-1 pt-1.5 text-[12px] font-medium text-muted" aria-hidden>
          Stage type
        </div>
        <div
          className="flex gap-1 px-2.5 pb-2"
          role="group"
          aria-label="Stage type"
          onKeyDown={(e) => {
            const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
            if (!step) return;
            e.preventDefault();
            const i = STAGE_KINDS.findIndex((k) => k.value === kind);
            const next = STAGE_KINDS[(i + step + STAGE_KINDS.length) % STAGE_KINDS.length]!;
            pickKind(next.value, false);
            e.currentTarget.querySelector<HTMLElement>(`[data-kind="${next.value}"]`)?.focus();
          }}
        >
          {STAGE_KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              role="menuitemradio"
              aria-checked={kind === k.value}
              tabIndex={kind === k.value ? 0 : -1}
              data-kind={k.value}
              title={k.hint}
              onClick={() => pickKind(k.value, true)}
              className={`min-h-10 flex-1 rounded-md border px-2 text-[12px] font-medium ${
                kind === k.value ? 'border-navy bg-navy text-white' : 'border-gray-200 text-gray-700 hover:bg-page'
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>

        <Separator />
        <MenuItem danger onSelect={() => { close(); admin.requestDelete(stage, count); }}>
          <Trash2 size={14} strokeWidth={2} aria-hidden /> Delete stage…
        </MenuItem>
      </Menu>
    </div>
  );
}
